import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { eventService } from '../../../shared/services/tenant/eventService';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { RefactoredPublicEventInfo } from '../components/RefactoredPublicEventInfo';
import { RegisterParticipantForm } from '../components/RegisterParticipantForm';
import { EventHeader } from '../components/EventHeader';
import { EventBanner } from '../components/EventBanner';
import Footer from '../components/Footer';
import { theme as customTheme, headerStyles } from '../constants/styles';
import { mergeTheme, generateThemeCSSVariables } from '../types';

export default function PublicEventDetailPage() {
  const { id, slug } = useParams();
  const idOrSlug = slug || id;
  const muiTheme = useTheme();
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'));

  const { data: event, isLoading } = useQuery({
    queryKey: ['public-event', idOrSlug],
    queryFn: () => eventService.getPublic(idOrSlug!).then(res => res.data),
    enabled: !!idOrSlug,
  });

  useEffect(() => {
    if (event?.title) {
      document.title = `${event.title} | AVIVA`;

      const updateMetaTag = (property: string, content: string) => {
        let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('property', property);
          document.head.appendChild(meta);
        }
        meta.content = content;
      };

      updateMetaTag('og:title', event.title);
      updateMetaTag('og:description', event.description?.substring(0, 160) || 'Inscreva-se neste evento!');
      updateMetaTag('og:type', 'website');
      if (event.image) {
        updateMetaTag('og:image', event.image);
      }
    }

    return () => {
      document.title = 'AVIVA - Gestão de Eventos';
    };
  }, [event]);

  if (isLoading) return null;
  if (!event) return <Typography color="error" sx={{ fontFamily: customTheme.fonts.primary }}>Evento não encontrado</Typography>;

  const registrationDeadline = event.registration_deadline
    ? new Date(
        new Date(event.registration_deadline).getFullYear(),
        new Date(event.registration_deadline).getMonth(),
        new Date(event.registration_deadline).getDate(),
        23, 59, 59,
      )
    : null;

  const mergedTheme = mergeTheme(event.theme_customization);
  const themeCSSVars = generateThemeCSSVariables(mergedTheme);
  const backgroundColor = headerStyles.logoPanel.bgcolor || '#f5f5f5';
  const themeStyles = {
    ...themeCSSVars,
    bgcolor: backgroundColor,
    fontFamily: mergedTheme.typography?.fontFamily || customTheme.fonts.primary,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: backgroundColor,
        ...themeStyles,
      }}
    >
      <EventHeader
        registrationDeadline={registrationDeadline}
        themeCustomization={event.theme_customization}
      />

      {event.image && !isDesktop && (
        <Box sx={{ width: '100%', px: 2, pt: 2, pb: 0, bgcolor: backgroundColor }}>
          <EventBanner image={event.image} title={event.title} />
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          width: '100%',
          maxWidth: '100%',
          padding: { xs: 2, md: 6 },
          bgcolor: backgroundColor,
          fontFamily: customTheme.fonts.primary,
          pt: { xs: event.image && !isDesktop ? 2 : 2, md: 6 },
        }}
      >
        <Box
          sx={{
            bgcolor: mergedTheme.colors?.secondaryBackgroundColor || '#F5F5F5',
            width: '100%',
            px: { xs: 2, md: 6 },
            py: { xs: 3, md: 4 },
            mb: 3,
            fontFamily: mergedTheme.typography?.fontFamily || customTheme.fonts.primary,
            borderRadius: 4,
          }}
        >
          <Grid container spacing={2} sx={{ maxWidth: '100%', mx: 0, fontFamily: customTheme.fonts.primary }}>
            {isDesktop ? (
              <>
                <Grid item xs={12} md={6}>
                  <RefactoredPublicEventInfo event={event} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ position: { md: 'sticky' }, top: { md: 24 }, fontFamily: customTheme.fonts.primary }}>
                    <RegisterParticipantForm eventId={event.id} event={event} />
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <Box sx={{ position: { md: 'sticky' }, top: { md: 24 }, fontFamily: customTheme.fonts.primary }}>
                    <RegisterParticipantForm eventId={event.id} event={event} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <RefactoredPublicEventInfo event={event} />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}
